<template>
  <button
    class="st-icon-btn"
    :class="[
      `st-icon-btn--${variant}`,
      `st-icon-btn--${size}`,
      { 'st-icon-btn--on-shell': onShell, 'is-active': active, 'st-icon-btn--tipped': tip },
    ]"
    :type="type"
    :disabled="disabled"
    :aria-label="label"
    :title="tip ? undefined : label"
    :aria-pressed="active ? 'true' : undefined"
  >
    <font-awesome-icon v-if="icon" :icon="['fas', icon]" />
    <slot v-else />
    <span v-if="tip" class="st-icon-btn__tip" :class="`st-icon-btn__tip--${tipPlacement}`">
      {{ label }}
    </span>
  </button>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    icon?: string;
    /** Accessible name — also used as the native tooltip. */
    label: string;
    variant?: 'default' | 'accent' | 'danger';
    size?: 'sm' | 'md';
    onShell?: boolean;
    /** Toggled/selected state (e.g. an active tool). */
    active?: boolean;
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    /** Show a styled hover tooltip (replaces the sluggish native title). */
    tip?: boolean;
    /** Side the styled tooltip pops out toward. */
    tipPlacement?: 'top' | 'right' | 'bottom' | 'left';
  }>(),
  { variant: 'default', size: 'md', type: 'button', tipPlacement: 'top' },
);
</script>

<style scoped>
.st-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--st-control-h-md);
  height: var(--st-control-h-md);
  border: 1px solid transparent;
  border-radius: var(--st-radius-sm);
  background: transparent;
  color: var(--st-text-muted);
  font-size: var(--st-text-md);
  cursor: pointer;
  transition: var(--st-transition);
}
.st-icon-btn--sm {
  width: var(--st-control-h-sm);
  height: var(--st-control-h-sm);
  font-size: var(--st-text-sm);
}
.st-icon-btn:hover:not(:disabled) {
  background: var(--st-surface-hover);
  color: var(--st-text);
}
.st-icon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.st-icon-btn.is-active {
  background: var(--st-accent-tint);
  color: var(--st-accent);
}
.st-icon-btn--accent:hover:not(:disabled) {
  color: var(--st-accent);
}
.st-icon-btn--danger:hover:not(:disabled) {
  background: var(--st-danger-tint);
  color: var(--st-danger);
}

/* ---- on the dark shell ---- */
.st-icon-btn--on-shell {
  color: var(--st-shell-text-muted);
}
.st-icon-btn--on-shell:hover:not(:disabled) {
  background: var(--st-shell-surface-hover);
  color: var(--st-shell-text);
}
.st-icon-btn--on-shell.is-active {
  background: var(--st-shell-surface-active);
  color: var(--st-accent-on-dark);
}
.st-icon-btn--on-shell.st-icon-btn--danger:hover:not(:disabled) {
  background: var(--st-danger);
  color: var(--st-danger-text);
}

/* ---- styled hover tooltip ----
   Pops instantly on hover/focus, avoiding the native title's sluggish delay. */
.st-icon-btn--tipped {
  position: relative;
}
.st-icon-btn__tip {
  position: absolute;
  z-index: var(--st-z-popover, 100);
  padding: var(--st-space-1) var(--st-space-2);
  border-radius: var(--st-radius-sm);
  background: var(--st-shell-surface-active);
  border: 1px solid var(--st-shell-border);
  color: var(--st-shell-text);
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-semibold);
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity var(--st-transition);
  box-shadow: var(--st-shadow-md, 0 4px 12px rgb(0 0 0 / 0.3));
}
.st-icon-btn__tip--right {
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
}
.st-icon-btn__tip--left {
  right: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
}
.st-icon-btn__tip--top {
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
}
.st-icon-btn__tip--bottom {
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
}
.st-icon-btn:hover .st-icon-btn__tip,
.st-icon-btn:focus-visible .st-icon-btn__tip {
  opacity: 1;
}
</style>
